import React, { useEffect, useState } from "react";

import {
  BrowserRouter,
  Routes,
  Route,
  NavLink,
  Navigate
} from "react-router-dom";

import "./App.css";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import ContactAdmin from "./pages/ContactAdmin";
import History from "./pages/History";

import Dashboard from "./pages/Dashboard";
import FoodCommittee from "./pages/sectors/FoodCommittee";
import Visitors from "./pages/sectors/Visitors";
import Vehicles from "./pages/sectors/Vehicles";
import PolicyMatters from "./pages/sectors/PolicyMatters";
import Transportation from "./pages/sectors/Transportation";
import Agreements from "./pages/sectors/Agreements";
import Security from "./pages/sectors/Security";
import CSR from "./pages/sectors/Csr";
import Cleaning from "./pages/sectors/Cleaning";
import Accident from "./pages/sectors/Accident";
import TransportCommittee from "./pages/sectors/TransportCommittee";
import UserManagement from "./pages/UserManagement";

import { hasInterfaceAccess } from "./utils/auth";

// =====================================
// SECTOR MODULES - single source of truth for sidebar links AND
// route access control. "module" here must exactly match the
// strings used in UserManagement's ALL_INTERFACES / assignedInterfaces.
// =====================================
const SECTOR_MODULES = [
  { path: "/dashboard/food", label: "Food Committee", module: "Food Committee", Component: FoodCommittee },
  { path: "/dashboard/visitors", label: "Visitors", module: "Visitors", Component: Visitors },
  { path: "/dashboard/vehicles", label: "Vehicles", module: "Vehicles", Component: Vehicles },
  { path: "/dashboard/policies", label: "Policy Matters", module: "Policy Matters", Component: PolicyMatters },
  { path: "/dashboard/transportation", label: "Transportation", module: "Transportation", Component: Transportation },
  { path: "/dashboard/agreements", label: "Agreements / Insurance", module: "Agreements / Insurance", Component: Agreements },
  { path: "/dashboard/security", label: "Security", module: "Security", Component: Security },
  { path: "/dashboard/csr", label: "CSR", module: "CSR", Component: CSR },
  { path: "/dashboard/cleaning", label: "Cleaning & Repairs", module: "Cleaning & Repairs", Component: Cleaning },
  { path: "/dashboard/accident", label: "Accident Records", module: "Accident Records", Component: Accident },
  { path: "/dashboard/transportcommittee", label: "Transport Committee", module: "Transport Committee", Component: TransportCommittee },
];

function App() {

  const role = localStorage.getItem("role");
  const username = localStorage.getItem("username");
  const profilePicture = localStorage.getItem("profilePicture");

  // =====================================
  // LIVE CLOCK
  // =====================================

  const [now, setNow] = useState(new Date());

  useEffect(() => {

    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(timer);

  }, []);

  const formattedDate = now.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const formattedTime = now.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const initials = (username || "?").trim().slice(0, 2).toUpperCase();

  // =====================================
  // DARK MODE
  // =====================================

  const [theme, setTheme] = useState(
    localStorage.getItem("theme") || "light"
  );

  useEffect(() => {

    if (theme === "dark") {

      document.body.classList.add("dark-mode");

    } else {

      document.body.classList.remove("dark-mode");
    }

    localStorage.setItem("theme", theme);

  }, [theme]);

  const toggleTheme = () => {

    setTheme(
      theme === "dark"
        ? "light"
        : "dark"
    );
  };

  // =====================================
  // LOGOUT
  // =====================================

  const logout = () => {

    localStorage.clear();

    window.location.href = "/";
  };

  return (

    <BrowserRouter>

      <Routes>

        <Route path="/" element={<Login />} />

        <Route
          path="/register"
          element={<Register />}
        />

        <Route
          path="/contact-admin"
          element={<ContactAdmin />}
        />

        <Route
          path="/profile"
          element={<Profile />}
        />

        <Route
          path="/history"
          element={<History />}
        />

        {/* ===================================== */}
        {/* MAIN DASHBOARD */}
        {/* ===================================== */}

        <Route
          path="/*"
          element={

            <div className="app-layout">

              {/* SIDEBAR */}

              <div className="sidebar">

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      background: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      overflow: "hidden",
                      padding: "4px",
                      boxSizing: "border-box",
                    }}
                  >
                    <img
                      src="/swisstek-logo.png"
                      alt="Swisstek"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </div>

                  <h2
                    className="logo"
                    style={{ margin: 0, fontSize: "18px", lineHeight: 1.2 }}
                  >
                    Company Dashboard
                  </h2>
                </div>

                <div
                  style={{
                    fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.65)",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "10px",
                    padding: "8px 12px",
                    marginBottom: "18px",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "8px",
                  }}
                >
                  <span>{formattedDate}</span>
                  <span>{formattedTime}</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "12px",
                    padding: "10px 12px",
                    marginBottom: "22px",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      background: "rgba(255,255,255,0.12)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "700",
                      fontSize: "13px",
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                  >
                    {profilePicture ? (
                      <img
                        src={profilePicture}
                        alt={username || "Profile"}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      initials
                    )}
                  </div>

                  <div style={{ overflow: "hidden" }}>
                    <div
                      style={{
                        color: "white",
                        fontWeight: "700",
                        fontSize: "13.5px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {username || "Unknown user"}
                    </div>

                    <span
                      style={{
                        display: "inline-block",
                        marginTop: "3px",
                        fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
                        fontSize: "10px",
                        fontWeight: "600",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: "white",
                        background: "rgba(255,255,255,0.16)",
                        padding: "2px 8px",
                        borderRadius: "999px",
                      }}
                    >
                      {role}
                    </span>
                  </div>
                </div>

                <NavLink
                  to="/dashboard"
                  className="nav-link"
                >
                  Dashboard
                </NavLink>

                {/* SECTOR MODULES - only rendered if this user has access */}

                {SECTOR_MODULES.filter((sector) =>
                  hasInterfaceAccess(sector.module)
                ).map((sector) => (
                  <NavLink
                    key={sector.path}
                    to={sector.path}
                    className="nav-link"
                  >
                    {sector.label}
                  </NavLink>
                ))}

                <NavLink
                  to="/profile"
                  className="nav-link"
                >
                  My Profile
                </NavLink>

                {/* ADMIN & SUPER ADMIN ONLY */}

                {(role === "admin" || role === "superadmin") && (

                  <NavLink
                    to="/dashboard/users"
                    className="nav-link"
                  >
                    User Management
                  </NavLink>

                )}

                {(role === "admin" || role === "superadmin") && (

                  <NavLink
                    to="/history"
                    className="nav-link"
                  >
                    App History
                  </NavLink>

                )}

                {/* DARK MODE BUTTON */}

                <button
                  onClick={toggleTheme}
                  style={{
                    marginTop: "15px",
                    padding: "12px",
                    border: "none",
                    borderRadius: "10px",
                    background: "#334155",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                    width: "100%"
                  }}
                >
                  {
                    theme === "dark"
                      ? "Light Mode"
                      : "Dark Mode"
                  }
                </button>

                {/* LOGOUT BUTTON */}

                <button
                  onClick={logout}
                  style={{
                    marginTop: "15px",
                    padding: "12px",
                    border: "none",
                    borderRadius: "10px",
                    background: "#ff4d4f",
                    color: "white",
                    fontWeight: "bold",
                    cursor: "pointer",
                    width: "100%"
                  }}
                >
                  Logout
                </button>

              </div>

              {/* MAIN CONTENT */}

              <div className="main-content">

                <Routes>

                  <Route
                    path="/dashboard"
                    element={<Dashboard />}
                  />

                  {/* SECTOR MODULE ROUTES - each checks hasInterfaceAccess
                      before rendering. If the user types the URL directly
                      without access, they're bounced back to /dashboard. */}

                  {SECTOR_MODULES.map((sector) => (
                    <Route
                      key={sector.path}
                      path={sector.path}
                      element={
                        hasInterfaceAccess(sector.module) ? (
                          <sector.Component />
                        ) : (
                          <Navigate to="/dashboard" replace />
                        )
                      }
                    />
                  ))}

                  <Route
                    path="/dashboard/users"
                    element={
                      role === "admin" || role === "superadmin" ? (
                        <UserManagement />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    }
                  />

                  <Route
                    path="/profile"
                    element={<Profile />}
                  />

                  <Route
                    path="/history"
                    element={
                      role === "admin" || role === "superadmin" ? (
                        <History />
                      ) : (
                        <Navigate to="/dashboard" replace />
                      )
                    }
                  />

                  <Route
                    path="*"
                    element={<Navigate to="/dashboard" />}
                  />

                </Routes>

              </div>

            </div>
          }
        />

      </Routes>

    </BrowserRouter>
  );
}

export default App;